import {LitElement, html, css} from 'lit';

class FilePicker extends LitElement {
  constructor() {
    super();

    this._file = null;
    this.accept = [];
    this.errorMessage = null;

    this.isFirstRender = true;
    this.switchFocus = false;
  }

  // No shadow root; older versions of safari don't support ElementInternals and the polyfill doesn't support File objects
  createRenderRoot() {
    return this;
  }

  fileValidation() {
    if (!this._file && this.required) {
      return [false, 'Please select a file.'];
    }

    if (!this._file) {
      return [true, null];
    }

    if (this.accept.length > 0 && !this.accept.includes(this._file.type)) {
      return [false, 'Invalid file type.'];
    }

    if (this._file && !this._file.size) {
      return [false, 'Please select a valid file.'];
    }

    if (this._file && this._file.size > 20000000) {
      return [false, 'File is too large, please limit uploads to 20MB or smaller.'];
    }

    return [true, null];
  }

  updated(_changedProperties) {
    super.updated(_changedProperties);

    if (_changedProperties.has('_file')) {
      if (this.switchFocus) {
        this.focus();
      }
    }

    this.switchFocus = false;
    this.isFirstRender = false;
  }

  handleChange(e) {
    this.switchFocus = true;
    if (!e.target.files || !e.target.files.length) {
      this._file = null;
    } else {
      this._file = e.target.files[0];
    }
  }

  focus() {
    if (this._file) {
      this.renderRoot.querySelector('.remove').focus();
    } else {
      this.renderRoot.querySelector('.button').focus();
    }
  }

  removeFile() {
    const inputElement = this.renderRoot.querySelector('input[type="file"]');

    this._file = null;
    this.switchFocus = true;

    inputElement.value = null;
    inputElement.dispatchEvent(new Event('change', {bubbles: true}));
  }

  toggleInput() {
    this.renderRoot.querySelector('input[type="file"]').click();
  }

  render() {
    return html`
      <input type="file" hidden accept="${this.accept.join(',')}" ?required="${this.required}"
        name="${this.name}"
        .customValidation=${this.fileValidation.bind(this)}
        @change="${this.handleChange}"/>
      ${this._file ? html`
        <div class="preview">
          <div class="name-wrapper">
            <svg aria-hidden="true" class="file-icon" viewBox="0 0 24 24" fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M20.024 7.713L14.78 2.47l-.023-.022-.03-.027-.023-.017-.037-.027-.018-.011a.727.727 0 00-.045-.027l-.013-.006a.724.724 0 00-.053-.026l-.01-.003a.749.749 0 00-.06-.021l-.008-.003a.75.75 0 00-.062-.015l-.02-.003-.053-.008a.741.741 0 00-.075-.004h-9a1.502 1.502 0 00-1.5 1.5v16.5a1.502 1.502 0 001.5 1.5h13.5a1.502 1.502 0 001.5-1.5v-12a.746.746 0 00-.226-.537zM15 16.5H9A.75.75 0 119 15h6a.75.75 0 110 1.5zm0-3H9A.75.75 0 119 12h6a.75.75 0 110 1.5zm-.75-5.25V4.06l4.19 4.19h-4.19z"
                fill="currentColor"></path>
            </svg>
            <p class="name">${this._file.name}</p>
          </div>
          <button class="remove" type="button" title="Remove uploaded file" @click="${this.removeFile}">
            <svg aria-hidden="true" class="delete-icon" viewBox="0 0 24 24" fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path d="M18.75 5.25l-13.5 13.5m13.5 0L5.25 5.25" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
          </button>
        </div>
      ` : html`
        <button type="button" class="button" @click="${this.toggleInput}">Select file</button>
      `}
    `;
  }
}

FilePicker.styles = css`
`;
FilePicker.properties = {
  accept: {
    type: Array,
    converter: {
      fromAttribute: (value) => value ? value.split(',').map(x => x.trim()) : [],
      toAttribute: (value) => (value || []).join(','),
    },
  },
  required: {
    type: Boolean,
  },
  _file: {
    state: true,
    attribute: false,
  },
  name: {
    type: String,
  },
  errorMessage: {
    state: true,
    attribute: false,
  },
};

customElements.define('kg-file-picker', FilePicker);
